import { $api } from '~/utils/api'
import { logApiError } from '~/utils/logger'
import { buildQueryString } from '~/utils/queryParams'

export interface FeestFeed {
  id: string
  title: string
  text: string
  image_url: string | null
  color: string | null
  span: string | null
  status: 'draft' | 'published' | 'archived'
  display_order: number | null
  created_at: string | null
  created_by: string | null
  updated_at: string | null
  updated_by: string | null
}

export interface FeestFeedInput {
  title: string
  text: string
  image_url?: string | null
  color?: string | null
  span?: 'normal' | 'tall'
  status?: 'draft' | 'published' | 'archived'
  display_order?: number
}

interface FeestFeedFilters {
  page?: number
  limit?: number
  search?: string
  status?: 'draft' | 'published' | 'archived'
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  publicOnly?: boolean
}

interface FeestFeedListResponse {
  data: FeestFeed[]
  metadata: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export const useFeestFeeds = () => {
  /**
   * Get all feest feeds (admin view - includes drafts/archived)
   */
  const getFeestFeeds = async (filters: FeestFeedFilters = {}): Promise<FeestFeedListResponse> => {
    try {
      const queryString = buildQueryString(filters)
      const url = `/api/feest-feeds${queryString ? `?${queryString}` : ''}`

      return await $api<FeestFeedListResponse>(url)
    }
    catch (error) {
      logApiError('/api/feest-feeds', error, { filters })
      throw error
    }
  }

  /**
   * Get published feest feeds only (public view)
   */
  const getPublishedFeeds = async (filters: Omit<FeestFeedFilters, 'status'> = {}): Promise<FeestFeedListResponse> => {
    return getFeestFeeds({ ...filters, publicOnly: true })
  }

  /**
   * Create a new feest feed
   */
  const createFeestFeed = async (data: FeestFeedInput): Promise<FeestFeed> => {
    try {
      const url = '/api/feest-feeds'

      return await $api<FeestFeed>(url, {
        method: 'POST',
        body: data,
      })
    }
    catch (error) {
      logApiError('/api/feest-feeds', error, { data })
      throw error
    }
  }

  /**
   * Update a feest feed
   */
  const updateFeestFeed = async (id: string, data: Partial<FeestFeedInput>): Promise<FeestFeed> => {
    try {
      const url = `/api/feest-feeds/${id}`

      return await $api<FeestFeed>(url, {
        method: 'PATCH',
        body: data,
      })
    }
    catch (error) {
      logApiError(`/api/feest-feeds/${id}`, error, { data })
      throw error
    }
  }

  /**
   * Delete a feest feed
   */
  const deleteFeestFeed = async (id: string): Promise<{ success: boolean }> => {
    try {
      const url = `/api/feest-feeds/${id}`

      return await $api<{ success: boolean }>(url, {
        method: 'DELETE',
      })
    }
    catch (error) {
      logApiError(`/api/feest-feeds/${id}`, error)
      throw error
    }
  }

  /**
   * Update feest feed status
   */
  const updateFeestFeedStatus = async (id: string, status: 'draft' | 'published' | 'archived'): Promise<FeestFeed> => {
    return updateFeestFeed(id, { status })
  }

  return {
    getFeestFeeds,
    getPublishedFeeds,
    createFeestFeed,
    updateFeestFeed,
    deleteFeestFeed,
    updateFeestFeedStatus,
  }
}
